/**
 * Utility functions for country whitelist editor
 */

interface Country {
  name?: string;
  code?: string;
}

/**
 * Filters countries based on search term
 */
export const filterCountriesBySearch = <T extends Country>(
  countries: T[],
  searchTerm: string | null | undefined,
): T[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return countries;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  return countries.filter(
    (country) =>
      (country.name && country.name.toLowerCase().includes(lowerSearchTerm)) ||
      (country.code && country.code.toLowerCase().includes(lowerSearchTerm)),
  );
};

/**
 * Calculates which countries were added compared to the original list
 */
export const getAddedCountries = (
  currentCountries: string[],
  selectedCountries: Set<string>,
): string[] => {
  return Array.from(selectedCountries).filter((code) => !currentCountries.includes(code));
};

/**
 * Calculates which countries were removed compared to the original list
 */
export const getRemovedCountries = (
  currentCountries: string[],
  selectedCountries: Set<string>,
): string[] => {
  return currentCountries.filter((code) => !selectedCountries.has(code));
};

/**
 * Toggles a country code in a Set (adds if not present, removes if present)
 */
export const toggleCountryInSet = (
  countrySet: Set<string>,
  countryCode: string,
): Set<string> => {
  const newSet = new Set(countrySet);
  if (newSet.has(countryCode)) {
    newSet.delete(countryCode);
  } else {
    newSet.add(countryCode);
  }
  return newSet;
};

/**
 * Formats embargoed countries into a display string
 */
export const formatEmbargoedCountries = (
  embargoedCountries: Record<string, string>,
  getCountryName: (code: string) => string,
): string => {
  return Object.keys(embargoedCountries)
    .map((code) => getCountryName(code))
    .join(', ');
};
